import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

export type ToastVariant = 'success' | 'error' | 'info';

interface ToastMessage {
  id: number;
  text: string;
  variant: ToastVariant;
}

const listeners = new Set<(t: ToastMessage) => void>();
let counter = 0;

/**
 * Show a transient snackbar. Mirrors the native `ScaffoldMessenger` snackbars
 * shown after batch dock actions, dead calls, etc.
 */
export function showToast(text: string, variant: ToastVariant = 'info'): void {
  const msg: ToastMessage = { id: ++counter, text, variant };
  for (const l of listeners) l(msg);
}

const variantClasses: Record<ToastVariant, string> = {
  success: 'bg-brand-delivery text-white',
  error: 'bg-brand-error text-white',
  info: 'bg-brand-surface text-brand-text',
};

/** Mount once near the app root. Renders queued toasts and auto-dismisses them. */
export function ToastHost() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  useEffect(() => {
    const onMsg = (t: ToastMessage) => {
      setToasts((prev) => [...prev, t]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((x) => x.id !== t.id));
      }, 2800);
    };
    listeners.add(onMsg);
    return () => {
      listeners.delete(onMsg);
    };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className="bottom-4-safe pointer-events-none fixed inset-x-0 z-[60] flex flex-col items-center gap-2 px-4">
      {toasts.map((t) => (
        <div
          key={t.id}
          role="status"
          className={cn(
            'pointer-events-auto w-full max-w-md rounded-lg px-4 py-3 text-center text-sm font-semibold shadow-lg',
            variantClasses[t.variant],
          )}
        >
          {t.text}
        </div>
      ))}
    </div>
  );
}
